import { endpoints, baseurl } from "../Utils/Constants";

// ---------------------------------------------------------------------------
// In-memory cache for getAllAegisAiAssistConfigs (60-second TTL).
// Only the list endpoint is cached — getAegisAiAssistConfigById is always live.
// The cache is invalidated automatically whenever a config is saved or
// deleted so the list reflects the latest state after any mutation.
// ---------------------------------------------------------------------------
const _assistListCache = { data: null, token: null, timestamp: 0 };
const ASSIST_CACHE_TTL_MS = 60_000; // 60 seconds

const _isCacheValid = (token) =>
    _assistListCache.token === token &&
    _assistListCache.data !== null &&
    Date.now() - _assistListCache.timestamp < ASSIST_CACHE_TTL_MS;

export const invalidateAegisAiAssistCache = () => {
    _assistListCache.data = null;
    _assistListCache.token = null;
    _assistListCache.timestamp = 0;
};

export const getAllAegisAiAssistConfigs = async (token) => {
    if (_isCacheValid(token)) {
        return _assistListCache.data;
    }
    try {
        const response = await fetch(baseurl + endpoints.GET_ALL_AEGIS_AI_ASSIST_CONFIGS, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });
        const json = await response.json();
        // Only cache successful responses.
        if (json?.is_success) {
            _assistListCache.data = json;
            _assistListCache.token = token;
            _assistListCache.timestamp = Date.now();
        }
        return json;
    } catch (error) {
        console.error("Error fetching Aegis AI assist configs:", error);
        throw error;
    }
};

export const getAegisAiAssistConfigById = async (id, token) => {
    try {
        const response = await fetch(`${baseurl + endpoints.GET_AEGIS_AI_ASSIST_CONFIG_BY_ID}${id}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });
        return await response.json();
    } catch (error) {
        console.error(`Error fetching Aegis AI assist config with ID ${id}:`, error);
        throw error;
    }
};

export const saveAegisAiAssistConfig = async (configData, token) => {
    try {
        const response = await fetch(baseurl + endpoints.SAVE_AEGIS_AI_ASSIST_CONFIG, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(configData)
        });
        const json = await response.json();
        // Invalidate the list cache so the next getAll returns fresh data.
        invalidateAegisAiAssistCache();
        return json;
    } catch (error) {
        console.error("Error saving Aegis AI assist config:", error);
        throw error;
    }
};

export const uploadAegisAiAssistIcon = async (id, file, token) => {
    try {
        const formData = new FormData();
        formData.append("icon", file);
        const response = await fetch(`${baseurl + endpoints.UPLOAD_AEGIS_AI_ASSIST_ICON}${id}`, {
            method: "POST",
            headers: {
                "Authorization": `Bearer ${token}`
            },
            body: formData
        });
        const json = await response.json();
        invalidateAegisAiAssistCache();
        return json;
    } catch (error) {
        console.error(`Error uploading icon for Aegis AI assist config with ID ${id}:`, error);
        throw error;
    }
};

export const removeAegisAiAssistIcon = async (id, token) => {
    try {
        const response = await fetch(`${baseurl + endpoints.REMOVE_AEGIS_AI_ASSIST_ICON}${id}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });
        const json = await response.json();
        invalidateAegisAiAssistCache();
        return json;
    } catch (error) {
        console.error(`Error removing icon for Aegis AI assist config with ID ${id}:`, error);
        throw error;
    }
};

// Public, unauthenticated URL of an agent's custom logo (null when it uses a preset icon).
export const getAegisAiAssistIconUrl = (id, iconFileId) =>
    id && iconFileId ? `${baseurl + endpoints.AEGIS_AI_PUBLIC_ASSIST}${id}/icon/${iconFileId}` : null;

export const deleteAegisAiAssistConfig = async (id, token) => {
    try {
        const response = await fetch(`${baseurl + endpoints.DELETE_AEGIS_AI_ASSIST_CONFIG}${id}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
            }
        });
        const json = await response.json();
        // Invalidate the list cache so the next getAll returns fresh data.
        invalidateAegisAiAssistCache();
        return json;
    } catch (error) {
        console.error(`Error deleting Aegis AI assist config with ID ${id}:`, error);
        throw error;
    }
};
